import { useCallback, useSyncExternalStore } from 'react';

/** Phones: below this, tables show "N. Destounis" instead of full names. */
export const NARROW_QUERY = '(max-width: 520px)';
/** Narrow screens, or any device whose primary pointer can't hover (touch). */
export const MOBILE_QUERY = '(max-width: 640px), (hover: none) and (pointer: coarse)';

// One MediaQueryList per query string, shared by every subscriber.
const cache = new Map<string, MediaQueryList>();

function getMql(query: string): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  let mql = cache.get(query);
  if (!mql) {
    mql = window.matchMedia(query);
    cache.set(query, mql);
  }
  return mql;
}

/** True while `query` matches; false where matchMedia is unavailable (SSR, jsdom). */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((onChange: () => void) => {
    const mql = getMql(query);
    if (!mql) return () => {};
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Legacy WebViews
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);
  const getSnapshot = useCallback(() => getMql(query)?.matches ?? false, [query]);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

export const useNarrowScreen = (): boolean => useMediaQuery(NARROW_QUERY);
export const useIsMobile = (): boolean => useMediaQuery(MOBILE_QUERY);
